import { FC, Fragment, useState, useEffect, useContext, Dispatch, SetStateAction } from 'react';
import ContextStore from '../../../../hooks/ContextStore';
import { IStoreResponseData } from '../../interfaces/responses/StoreResponseData.interface';
import { IErrorResponse } from '../../interfaces/responses/ErrorResponse.interface';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { t } from '../../i18n/ar';
import { isSoundEnabled, playGo, setSoundEnabled, unlockAudio } from '../../sound';

export type AppMode = 'scan' | 'packing' | 'find' | 'marjan' | 'returns' | 'alerts';
const SHOW_RETURNS = true;   // 2026-09-24: returns reception is the physical proof against the marketplace hand-over records

interface IHeaderProps {
  stores: Array<IStoreResponseData>;
  setStores: Dispatch<SetStateAction<Array<IStoreResponseData>>>;
  setShowSpinner: Dispatch<SetStateAction<boolean>>;
  mode: AppMode;
  setMode: Dispatch<SetStateAction<AppMode>>;
}

// Minimal header: the worker only needs the 🔊 toggle and the scan/packing
// switch. The ⚙️ menu is a READ-ONLY list of the connected stores — adding or
// deleting stores is done in the main dashboard only, so nothing here can be
// clicked by mistake.
export const Header: FC<IHeaderProps> = ({ stores, setStores, setShowSpinner, mode, setMode }) => {
  const [expandDropdown, setExpandDropdown] = useState<boolean>(false);
  const [sound, setSound] = useState<boolean>(isSoundEnabled());
  // 🚨 parcels handed over then cancelled, not back yet — red badge on the alerts button (refreshed every 5 min)
  const [alertCount, setAlertCount] = useState<number>(0);
  // 🚨 admin only for now (user 2026-10-21)
  const { contextStore } = useContext(ContextStore);
  const admin = contextStore?.user?.type === 'admin';
  useEffect(() => {
    if (!admin) return;
    const load = async () => {
      const r = await dispatch(actions.getHandoverAlerts, {}, { days: 60 });
      if (r && !('error' in r)) setAlertCount(r.counts?.notBack || 0);
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [mode, admin]);

  useEffect(() => {
    (async () => {
      setShowSpinner(true);
      const storesResp: Array<IStoreResponseData> | IErrorResponse = await dispatch(actions.getStores);
      if (!storesResp || 'error' in storesResp) { setShowSpinner(false); return console.log(storesResp?.error?.message); }
      setStores(storesResp);
      setShowSpinner(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSound = () => {
    const next = !sound;
    setSoundEnabled(next);
    setSound(next);
    if (next) { unlockAudio(); playGo(); }
  };

  return (
    <Fragment>
      <header className="w-full border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-3 py-2 sm:px-6">
          {/* Brand */}
          <div className="flex items-center gap-2 text-2xl font-black">
            <span aria-hidden>📷</span>
            <span style={{ color: '#f68b1e' }}>{t.appName}</span>
          </div>

          {/* Mode switch in WORK order: prepare → scan → find. One always lit. */}
          <div className="flex overflow-hidden rounded-xl border-2 border-slate-300">
            <button
              onClick={() => setMode('packing')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'packing' ? 'bg-[#f68b1e] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>📦</span>
              <span className="hidden sm:inline">{t.packMode}</span>
            </button>
            <button
              onClick={() => setMode('scan')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'scan' ? 'bg-[#f68b1e] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>📷</span>
              <span className="hidden sm:inline">{t.scanMode}</span>
            </button>
            {/* Marjan preparation — its own flow, teal so it never mixes with Jumia orange */}
            <button
              onClick={() => setMode('marjan')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'marjan' ? 'bg-[#00afaa] text-white' : 'bg-white text-[#00afaa] hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>🛒</span>
              <span className="hidden sm:inline">{t.marjanMode}</span>
            </button>
            {/* Returns scan mode is hidden for now (user, 2026-09-15) — logic kept in
                component/returns; set SHOW_RETURNS to true to bring the button back. */}
            {SHOW_RETURNS && <button
              onClick={() => setMode('returns')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'returns' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>🔁</span>
              <span className="hidden sm:inline">{t.retMode}</span>
            </button>}
            {admin && <button
              onClick={() => setMode('alerts')}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'alerts' ? 'bg-red-600 text-white' : 'bg-white text-red-600 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>🚨</span>
              <span className="hidden sm:inline">{t.alertMode}</span>
              {alertCount > 0 && (
                <span className={`rounded-full px-2 text-base font-black ${mode === 'alerts' ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>{alertCount}</span>
              )}
            </button>}
            <button
              onClick={() => setMode('find')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-lg font-black transition-colors ${
                mode === 'find' ? 'bg-[#f68b1e] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span className="text-2xl" aria-hidden>📍</span>
              <span className="hidden sm:inline">{t.findMode}</span>
            </button>
          </div>

          <div className="flex-1" />

          {/* Sound toggle — big, obvious, one tap */}
          <button
            onClick={toggleSound}
            title={t.sound}
            className={`flex items-center gap-2 rounded-xl border-2 px-3 py-1.5 text-lg font-bold transition-colors ${
              sound ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-100 text-slate-500'
            }`}
          >
            <span className="text-2xl" aria-hidden>{sound ? '🔊' : '🔇'}</span>
            <span className="hidden sm:inline">{t.sound}</span>
          </button>

          {/* Store management (admin) */}
          <div className="relative shrink-0">
            <button
              onClick={() => setExpandDropdown(!expandDropdown)}
              title={t.manageStores}
              className="flex items-center gap-1.5 rounded-xl border-2 border-slate-200 bg-white px-3 py-1.5 text-lg font-bold text-slate-600 transition-colors hover:bg-slate-50"
            >
              <span className="text-2xl" aria-hidden>⚙️</span>
              <span className="hidden sm:inline">{t.manageStores}</span>
              <span className="rounded-md bg-slate-100 px-1.5 text-sm text-slate-500">{stores.length}</span>
            </button>

            {expandDropdown && (
              <div className="absolute end-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="max-h-64 space-y-1 overflow-y-auto p-2">
                  {stores.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-slate-400">{t.noStores}</p>
                  )}
                  {stores.map((store) => (
                    <div key={store._id} className="flex items-center gap-2 rounded-lg px-3 py-2">
                      <span className="text-emerald-500" aria-hidden>●</span>
                      <span className="ltr flex-1 truncate text-start text-sm font-medium text-slate-700">{store.name}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-100 px-3 py-2 text-center text-xs text-slate-400">
                  {t.storesReadOnly}
                </div>
              </div>
            )}
          </div>

          {expandDropdown && (
            <div className="fixed inset-0 z-40" onClick={() => setExpandDropdown(false)} />
          )}
        </div>
      </header>
    </Fragment>
  );
};
